import type { ReactNode } from 'react'
import { useLocation, Navigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContextBackend'
import LoginForm from './LoginForm'
import RegisterForm from './RegisterForm'
import VerifyEmail from './VerifyEmail'
import { getCurrentSubdomain, getMainDomainLoginUrl, isMainDomain, buildSubdomainUrl, getBaseDomain, doesAccountMatchSubdomain } from '../utils/subdomain'

interface ProtectedRouteProps {
  children: ReactNode
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  // Allow external collaborator routes without authentication
  const isExternalCollaboratorRoute = location.pathname.includes('/collaborator/')

  // Also allow Job Builder edit routes when a collaborator token is present in the query string
  const searchParams = new URLSearchParams(location.search)
  const hasCollaboratorToken = !!searchParams.get('token')
  const hasCollaboratorMode = localStorage.getItem('collaborator_mode') === 'true'
  const isExternalCollaboratorEditRoute =
    location.pathname.includes('/comp-builder/job-builder/edit/') && (hasCollaboratorToken || hasCollaboratorMode)
  const isExternalCollaboratorTitlesRoute =
    location.pathname.includes('/comp-builder/job-builder/titles') && hasCollaboratorMode

  if (isExternalCollaboratorRoute || isExternalCollaboratorEditRoute || isExternalCollaboratorTitlesRoute) {
    return <>{children}</>
  }

  // Check for impersonation session in localStorage (handles race condition with state)
  const hasImpersonationSession = !!localStorage.getItem('impersonation_data')

  // Show loading screen while authentication is being checked
  if (isLoading && !hasImpersonationSession) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    )
  }

  // Allow access if impersonating (even if React state hasn't caught up)
  if (hasImpersonationSession) {
    return <>{children}</>
  }

  // Validate subdomain-account match (if on a client subdomain)
  if (getCurrentSubdomain() && isAuthenticated) {
    const storedAccount = JSON.parse(localStorage.getItem('account') || '{}')
    if (!doesAccountMatchSubdomain(storedAccount.domain)) {
      // User is on wrong subdomain (or account has no domain set) -- redirect to main domain login
      window.location.href = getMainDomainLoginUrl()
      return null
    }
  }

  // Not authenticated: redirect to login with redirect param so we can return after login
  if (!isAuthenticated) {
    if (location.pathname === '/register') {
      return <RegisterForm />
    }
    if (location.pathname === '/verify-email') {
      return <VerifyEmail />
    }
    if (location.pathname === '/login') {
      return <LoginForm />
    }
    // Preserve full path + query so after login we navigate back (e.g. IDP details from email link)
    const redirectParam = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?redirect=${redirectParam}`} replace />
  }

  // Auto-redirect authenticated users from main domain to their client subdomain
  // Skip for admin routes and impersonation (admin stays on main domain)
  // Skip on localhost (getBaseDomain() returns '' — no subdomain routing in dev)
  // Skip if subdomain bootstrap previously failed (prevents redirect loop)
  const bootstrapFailed = sessionStorage.getItem('subdomain_bootstrap_failed')
  if (getBaseDomain() && isMainDomain() && isAuthenticated && !location.pathname.startsWith('/tnadmin') && !bootstrapFailed) {
    const storedAccount = JSON.parse(localStorage.getItem('account') || '{}')
    if (storedAccount.domain) {
      const targetUrl = buildSubdomainUrl(storedAccount.domain, location.pathname + location.search)
      window.location.href = targetUrl
      return null
    }
  }

  // User is authenticated, show the protected content
  return <>{children}</>
}